import { message } from '@repo/ui';
import { translate, TranslationEntity } from '@repo/utils';
import { type NodeProps } from '@xyflow/react';
import { Button, Card, Flex } from 'antd';
import NodeMenu from 'automations/components/NodeMenu';
import { useAutomationDispatch } from 'automations/hooks/useAutomationDispatch';
import useAutomationId from 'automations/hooks/useAutomationId';
import useCommonNodeHandles from 'automations/hooks/useCommonNodeHandles';
import { addAdditionalOptions } from 'automations/store/reactFlowSlice';
import { type UserUpdateNodeType } from 'automations/types/nodeTypes';
import { saveTriggerNode } from 'automations/utils/triggerNode';
import { useCallback, useState } from 'react';
import { ZodError } from 'zod';

import styles from './UserUpdateMenu.module.scss';

export default function UserUpdateMenu({
  node,
}: {
  node: NodeProps<UserUpdateNodeType>;
}) {
  const automationId = useAutomationId();
  const dispatch = useAutomationDispatch();
  const { setMenuOpen, contentMode } = useCommonNodeHandles(node);
  const [isLoading, setIsLoading] = useState(false);

  const handleSave = useCallback(async () => {
    setIsLoading(true);
    try {
      dispatch(addAdditionalOptions({
        nodeId: node.id,
        additionalOptions: {},
      }));

      await dispatch(saveTriggerNode(node.id, automationId, contentMode));
      message.success(translate(`#{${TranslationEntity.automation}} updated`, [{ capitalize: true }]));
      setMenuOpen(false);
    } catch (error: any) {
      const errorMessage = error instanceof ZodError
        ? error.issues.map((issue) => issue.message).join(', ')
        : error?.data?.message ?? translate(`Failed to update #{${TranslationEntity.automation}}`);
      message.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [dispatch, node.id, automationId, contentMode, setMenuOpen]);

  return (
    <NodeMenu
      node={node}
      drawerProps={{
        title: translate(
          `#{${TranslationEntity.automation}} Trigger - When User is updated`,
          [{ capitalize: true }],
        ),
      }}
    >
      <Card title={contentMode === 'edit' ? 'Edit Trigger' : 'Set Trigger'} className={styles.containerCard}>
        <p>This automation will trigger whenever an existing user is updated in the system.</p>
      </Card>

      <Flex justify="flex-end" gap={16} className={styles.actionButton}>
        <Button onClick={() => setMenuOpen(false)}>Cancel</Button>
        <Button type="primary" onClick={handleSave} loading={isLoading} disabled={isLoading}>
          Set Trigger
        </Button>
      </Flex>
    </NodeMenu>
  );
}
